import { get } from 'lodash-es';
import { useState } from 'react';
import { type SubmitHandler, useForm, useWatch } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button, ButtonVariants } from '@components/button/Button';
import { ControlledFormDateRange } from '@components/formInputs/ControlledFormDateRange';
import { ControlledFormDayRadio } from '@components/formInputs/ControlledFormDayRadio';
import { ControlledFormInput } from '@components/formInputs/ControlledFormInput';
import { ControlledFormTextareaAutosize } from '@components/formInputs/ControlledFormTextareaAutosize';
import { errorToast } from '@components/toasts/Toasts';
import { ApiError } from '@shared/api/errors';
import { reportAppError } from '@shared/reportAppError';
import { todayInTimezone } from '@shared/utils/dateFormatters';
import { OPERATIONS_BULLETIN_BOARD_PATH } from 'restaurantAdmin/paths';
import typography from '~styles/typography.scss';
import { useRestaurant } from '../../context/useRestaurant';
import { PageContent } from '../../layout/PageContent';
import { PageHeader } from '../../layout/PageHeader';
import {
  createRestaurantBulletin,
  editRestaurantBulletin,
  type RestaurantBulletin,
} from './apiHelpers';
import styles from './RestaurantBulletinForm.scss';

interface RestaurantBulletinFormData {
  description: string;
  endDate: string;
  name: string;
  repeat: string[];
  startDate: string;
}

export const RestaurantBulletinForm = () => {
  const restaurant = useRestaurant();
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);

  const restaurantBulletin: RestaurantBulletin = get(
    location,
    'state.restaurantBulletin',
    {},
  );

  const { control, handleSubmit } = useForm<RestaurantBulletinFormData>({
    defaultValues: {
      description: restaurantBulletin.description,
      endDate: restaurantBulletin.endDate || undefined,
      name: restaurantBulletin.name,
      repeat: restaurantBulletin.repeat || [],
      startDate:
        restaurantBulletin.startDate || todayInTimezone(restaurant.timezone),
    },
  });

  const endDate = useWatch({ control, name: 'endDate' });
  const startDate = useWatch({ control, name: 'startDate' });
  const showRepeatInput = startDate && startDate !== endDate;

  const onValidSubmit: SubmitHandler<RestaurantBulletinFormData> = async (
    data,
  ) => {
    setIsLoading(true);

    const bulletinPayload = {
      ...data,
      endDate: data.endDate || null,
      name: data.name,
      repeat: startDate === endDate ? [] : data.repeat,
    };

    try {
      if (restaurantBulletin.id) {
        await editRestaurantBulletin(restaurant.id, {
          ...bulletinPayload,
          id: restaurantBulletin.id,
        });
      } else {
        await createRestaurantBulletin(restaurant.id, bulletinPayload);
      }
      navigate(OPERATIONS_BULLETIN_BOARD_PATH);
    } catch (e) {
      if (e instanceof ApiError) {
        errorToast({ message: e.message });
      }
      reportAppError(e);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDiscardClick = () => {
    navigate(OPERATIONS_BULLETIN_BOARD_PATH);
  };

  return (
    <>
      <PageHeader title="Bulletin Board" />
      <PageContent>
        <form
          aria-labelledby="form-title"
          className={styles.form}
          onSubmit={handleSubmit(onValidSubmit)}
        >
          <header>
            <h2 className={typography.h5} id="form-title">
              {restaurantBulletin.id ? 'Edit Bulletin' : 'New Bulletin'}
            </h2>
          </header>
          <ControlledFormInput
            control={control}
            disabled={false}
            label="Name"
            name="name"
            rules={{
              maxLength: {
                value: 50,
                message: 'Name must not exceed 50 characters',
              },
              required: true,
            }}
            type="text"
          />
          <ControlledFormTextareaAutosize
            control={control}
            disabled={false}
            label="Description"
            rules={{
              maxLength: {
                value: 500,
                message: 'Description must not exceed 500 characters',
              },
              minLength: {
                message: 'Description must be at least 4 characters',
                value: 4,
              },
              required: true,
            }}
            name="description"
          />
          <ControlledFormDateRange
            control={control}
            fromProps={{
              label: 'Start Date',
              name: `startDate`,
              rules: { required: true },
            }}
            toProps={{
              label: 'End Date',
              name: `endDate`,
              placeholder: 'Endless',
              rules: {
                min: {
                  value: startDate,
                  message: 'End Date must be on or after Start Date',
                },
              },
            }}
            groupLabel="Start and End"
          />
          {showRepeatInput && (
            <ControlledFormDayRadio
              className={styles.repeatInput}
              control={control}
              label="Repeats On"
              name="repeat"
              rules={{
                required: true,
              }}
            />
          )}
          <div className={styles.buttonContainer}>
            <span />
            <Button
              label="Discard Edits"
              onClick={handleDiscardClick}
              type="button"
              variant={ButtonVariants.Tertiary}
            />
            <Button
              isDisabled={isLoading}
              label="Save"
              type="submit"
              variant={ButtonVariants.Primary}
            />
          </div>
        </form>
      </PageContent>
    </>
  );
};
